import { SECTIONS, areaForSection, type AreaId } from './sections';

export type SourceRef = {
  path: string;
  sha256: string;
};

export type Article = {
  slug: string;
  section: string;
  title: string;
  order: number;
  summary: string;
  time: string;
  updated: string;
  body: string;
  // Generated/maintained by the docs-build workflow. Optional for
  // human-authored articles (welcome.md, what-is-schedata.md).
  source_refs?: SourceRef[];
  generated_by?: string;
  archived_at?: string;
  human_authored?: boolean;
};

export type Heading = {
  level: 2 | 3;
  text: string;
  id: string;
};

export type SidebarSection = {
  id: string;
  title: string;
  icon: string;
  area: AreaId;
  articles: Article[];
};

export type Sidebar = SidebarSection[];

// Browser-safe frontmatter parser (gray-matter's Node deps broke at runtime).
// Handles scalar key:value pairs and nested array-of-mappings (used by source_refs).
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) {
    return { data: {}, content: raw };
  }
  const [, block, content] = match;
  const data: Record<string, unknown> = {};
  const lines = block.split(/\r?\n/);

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Top-level scalar: "key: value"
    const scalar = line.match(/^([A-Za-z_][\w-]*)\s*:\s*(.*)$/);
    if (scalar) {
      const [, key, rawValue] = scalar;
      const value = rawValue.trim();

      // Empty value + indented children → parse as array-of-mappings.
      // We only support: list items are 2-space-indented "- path: …\n    sha256: …"
      // (used by source_refs). Anything else stays as a flat string.
      if (value === '' && i + 1 < lines.length && /^\s+-\s/.test(lines[i + 1])) {
        const items: Array<Record<string, string>> = [];
        let j = i + 1;
        while (j < lines.length) {
          const itemHeader = lines[j].match(/^\s+-\s+([A-Za-z_][\w-]*)\s*:\s*(.*)$/);
          if (!itemHeader) break;
          const item: Record<string, string> = {};
          item[itemHeader[1]] = unquote(itemHeader[2].trim());
          j += 1;
          while (j < lines.length) {
            const cont = lines[j].match(/^\s{4,}([A-Za-z_][\w-]*)\s*:\s*(.*)$/);
            if (!cont) break;
            item[cont[1]] = unquote(cont[2].trim());
            j += 1;
          }
          items.push(item);
        }
        if (items.length > 0) {
          data[key] = items;
          i = j;
          continue;
        }
      }

      // Plain scalar value
      data[key] = coerceScalar(value);
      i += 1;
      continue;
    }

    i += 1;
  }

  return { data, content };
}

function unquote(value: string): string {
  const m = value.match(/^(['"])(.*)\1$/);
  return m ? m[2] : value;
}

function coerceScalar(value: string): string | number | boolean {
  const quoted = value.match(/^(['"])(.*)\1$/);
  if (quoted) return quoted[2];
  if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value);
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === '') return '';
  return value;
}

export function slugify(s: string): string {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Extract h2/h3 headings from raw markdown body. Used for the right-rail TOC.
// Matches lines starting with `## ` or `### ` (not setext, not fenced-code).
export function extractHeadings(body: string): Heading[] {
  const headings: Heading[] = [];
  let inCodeFence = false;
  for (const line of body.split(/\r?\n/)) {
    if (/^```/.test(line)) {
      inCodeFence = !inCodeFence;
      continue;
    }
    if (inCodeFence) continue;
    const h2 = line.match(/^##\s+(.+?)\s*#*\s*$/);
    if (h2) {
      headings.push({ level: 2, text: h2[1], id: slugify(h2[1]) });
      continue;
    }
    const h3 = line.match(/^###\s+(.+?)\s*#*\s*$/);
    if (h3) {
      headings.push({ level: 3, text: h3[1], id: slugify(h3[1]) });
    }
  }
  return headings;
}

// Vite globs every .md file under src/content/ at build time and returns
// raw strings. Glob results in tests are injected explicitly via buildArticles.
// Exclude _archive/ and _images/ subtrees at the glob level — content under
// these paths is not part of the rendered help site (archived articles and
// per-article screenshot assets respectively).
const rawModules = import.meta.glob([
  '../content/**/*.md',
  '!../content/**/_archive/**',
  '!../content/**/_images/**',
], {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

export function buildArticles(modules: Record<string, string> = rawModules): Article[] {
  return Object.entries(modules)
    .filter(([path]) => !path.includes('/_archive/') && !path.includes('/_images/'))
    .map(([path, raw]) => {
    // path is like "../content/getting-started/welcome.md"
    // slug is the file basename (globally unique across the help site).
    const filename = path.split('/').pop() ?? '';
    const slug = filename.replace(/\.md$/, '');
    const parsed = parseFrontmatter(raw);
    const data = parsed.data;
    return {
      slug,
      section: typeof data.section === 'string' ? data.section : 'uncategorized',
      title: typeof data.title === 'string' ? data.title : slug,
      order: typeof data.order === 'number' ? data.order : 0,
      summary: typeof data.summary === 'string' ? data.summary : '',
      time: typeof data.time === 'string' ? data.time : '',
      updated: typeof data.updated === 'string' ? data.updated : '',
      body: parsed.content,
      source_refs: Array.isArray(data.source_refs)
        ? (data.source_refs as Array<Record<string, string>>)
            .filter(r => typeof r.path === 'string' && typeof r.sha256 === 'string')
            .map(r => ({ path: r.path, sha256: r.sha256 }))
        : undefined,
      generated_by: typeof data.generated_by === 'string' ? data.generated_by : undefined,
      archived_at: typeof data.archived_at === 'string' ? data.archived_at : undefined,
      human_authored: typeof data.human_authored === 'boolean' ? data.human_authored : undefined,
    };
  });
}

// Build sidebar respecting SECTIONS order. Sections defined in SECTIONS but
// with no articles still appear; articles whose `section` matches no SECTION
// are silently dropped (warned in the console).
export function buildSidebar(articles: Article[]): Sidebar {
  const bySection = new Map<string, Article[]>();
  for (const a of articles) {
    if (!bySection.has(a.section)) bySection.set(a.section, []);
    bySection.get(a.section)!.push(a);
  }
  // Warn for unknown sections in dev — easy authoring mistake.
  const known = new Set(SECTIONS.map(s => s.id));
  for (const sec of bySection.keys()) {
    if (!known.has(sec)) {
      console.warn(`[help-frontend] article uses unknown section id: "${sec}"`);
    }
  }
  return SECTIONS.map(s => ({
    id: s.id,
    title: s.title,
    icon: s.icon,
    area: s.area,
    articles: (bySection.get(s.id) ?? []).slice().sort((a, b) => a.order - b.order),
  }));
}

export function findArticle(articles: Article[], slug: string): Article | undefined {
  return articles.find(a => a.slug === slug);
}

/** The area an article belongs to, via its section. */
export function articleArea(article: Article): AreaId {
  return areaForSection(article.section);
}

/**
 * Canonical in-app URL for an article. API-area articles live under `/api/`;
 * everything else lives at the root. This is the single source of truth for
 * article links — sidebar, home tiles, search, and the pager all use it.
 */
export function articleHref(article: Article): string {
  return articleArea(article) === 'api' ? `/api/${article.slug}` : `/${article.slug}`;
}

// Screenshot/image assets that live next to articles in `<section>/_images/`.
// Globbed as URLs (not raw) so Vite fingerprints + serves them in dev and
// bundles them in prod. Article bodies reference them with relative paths like
// `./_images/foo.png`; resolveImage maps that to the real served URL using the
// article's section. Without this, react-markdown emits a bare
// `<img src="./_images/foo.png">` that the SPA can't serve (404 → broken img).
const imageModules = import.meta.glob(
  '../content/**/_images/*.{png,jpg,jpeg,gif,svg,webp,avif}',
  { eager: true, query: '?url', import: 'default' },
) as Record<string, string>;

export function resolveImage(section: string, src: string): string | undefined {
  // src like "./_images/foo.png" or "_images/foo.png" → file "foo.png"
  const file = src.replace(/^\.\//, '').replace(/^_images\//, '');
  return imageModules[`../content/${section}/_images/${file}`];
}

// Flat ordered list of articles, in section/order order. Used for prev/next.
export function orderedArticles(sidebar: Sidebar): Article[] {
  return sidebar.flatMap(s => s.articles);
}

// Pre-built at module load from the bundled glob.
export const articles: Article[] = buildArticles();
export const sidebar: Sidebar = buildSidebar(articles);
export const ordered: Article[] = orderedArticles(sidebar);

/** Sidebar sections for one area. */
export function sidebarForArea(area: AreaId): Sidebar {
  return sidebar.filter(s => s.area === area);
}

/** Flat ordered article list within one area. Used for area-local prev/next. */
export function orderedForArea(area: AreaId): Article[] {
  return ordered.filter(a => articleArea(a) === area);
}
